"use client";

import * as React from "react";
import { Checkbox } from "../primitives/checkbox";
import { Skeleton } from "../primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../primitives/table";
import { EmptyState } from "./empty-state";
import { ErrorState } from "./confirm-dialog";
import { cn } from "../primitives/cn";
import { ListPagination } from "./list-pagination";

export type DataTableColumn<T> = {
  id: string;
  header: string;
  sortable?: boolean;
  /** Desktop cell */
  cell: (row: T) => React.ReactNode;
  /** Mobile card body; defaults to cell */
  mobileCell?: (row: T) => React.ReactNode;
  className?: string;
};

export type DataTableSort = { id: string; desc: boolean } | null;

export type DataTableProps<T extends { id: string }> = {
  columns: DataTableColumn<T>[];
  rows: T[];
  status: "loading" | "empty" | "error" | "ready";
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  selectedIds?: Set<string>;
  onSelectedIdsChange?: (ids: Set<string>) => void;
  onRowClick?: (row: T) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  onRetry?: () => void;
  className?: string;
  /** v3 table shell + optional footer inside wrap */
  footer?: React.ReactNode;
};

/**
 * DataTable — sortable / paginated externally / multi-select.
 * Mobile (< md): card list (must not only scroll horizontally).
 */
export function DataTable<T extends { id: string }>({
  columns,
  rows,
  status,
  sort,
  onSortChange,
  selectedIds,
  onSelectedIdsChange,
  onRowClick,
  emptyTitle = "No data available",
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  onRetry,
  className,
  footer,
}: DataTableProps<T>) {
  const [autoPage, setAutoPage] = React.useState(1);
  const [autoPageSize, setAutoPageSize] = React.useState(25);
  const autoPaginate = footer == null && rows.length >= 10;
  const displayedRows = autoPaginate
    ? rows.slice((autoPage - 1) * autoPageSize, autoPage * autoPageSize)
    : rows;
  const selectable = Boolean(onSelectedIdsChange);
  const allSelected =
    selectable && rows.length > 0 && rows.every((r) => selectedIds?.has(r.id));

  const toggleAll = () => {
    if (!onSelectedIdsChange) return;
    if (allSelected) onSelectedIdsChange(new Set());
    else onSelectedIdsChange(new Set(rows.map((r) => r.id)));
  };

  const toggleOne = (id: string) => {
    if (!onSelectedIdsChange || !selectedIds) return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedIdsChange(next);
  };

  if (status === "loading") {
    return (
      <div className={cn("space-y-2", className)}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  if (status === "error") {
    return <ErrorState onRetry={onRetry} />;
  }

  if (status === "empty") {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        actionLabel={emptyActionLabel}
        onAction={onEmptyAction}
      />
    );
  }

  return (
    <div className={className}>
      {/* Desktop / tablet table */}
      <div className="hidden overflow-hidden rounded-lg border border-border bg-card md:block">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {selectable ? (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                ) : null}
                {columns.map((col) => (
                  <TableHead
                    key={col.id}
                    className={cn(
                      "text-[11px] font-medium uppercase tracking-wide text-muted-foreground",
                      col.className,
                    )}
                  >
                    {col.sortable && onSortChange ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => {
                          if (sort?.id === col.id) {
                            onSortChange(
                              sort.desc ? null : { id: col.id, desc: true },
                            );
                          } else {
                            onSortChange({ id: col.id, desc: false });
                          }
                        }}
                      >
                        {col.header}
                        {sort?.id === col.id ? (sort.desc ? " ↓" : " ↑") : null}
                      </button>
                    ) : (
                      col.header
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedRows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={selectedIds?.has(row.id) ? "selected" : undefined}
                  className={onRowClick ? "cursor-pointer" : undefined}
                  onClick={() => onRowClick?.(row)}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (onRowClick && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }}
                >
                  {selectable ? (
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      className="w-10"
                    >
                      <Checkbox
                        checked={selectedIds?.has(row.id)}
                        onCheckedChange={() => toggleOne(row.id)}
                        aria-label={`Select ${row.id}`}
                      />
                    </TableCell>
                  ) : null}
                  {columns.map((col) => (
                    <TableCell key={col.id} className={col.className}>
                      {col.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {footer ?? (autoPaginate ? (
          <ListPagination
            variant="footer"
            page={autoPage}
            pageSize={autoPageSize}
            total={rows.length}
            onPageChange={setAutoPage}
            onPageSizeChange={(size) => {
              setAutoPageSize(size);
              setAutoPage(1);
            }}
            pageSizeOptions={[10, 25, 50]}
          />
        ) : null)}
      </div>

      {/* Mobile card list */}
      <ul className="space-y-2 md:hidden">
        {displayedRows.map((row) => (
          <li key={row.id}>
            <div
              role={onRowClick ? "button" : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              className={cn(
                "w-full rounded-lg border border-border bg-card p-4 text-left transition-colors duration-micro hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                selectedIds?.has(row.id) && "bg-muted",
              )}
              onClick={() => onRowClick?.(row)}
              onKeyDown={(e) => {
                if (onRowClick && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onRowClick(row);
                }
              }}
            >
              <div className="mb-2 flex items-center gap-2">
                {selectable ? (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOne(row.id);
                    }}
                  >
                    <Checkbox checked={selectedIds?.has(row.id)} />
                  </span>
                ) : null}
              </div>
              <div className="space-y-2">
                {columns.map((col) => (
                  <div key={col.id} className="flex justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">{col.header}</span>
                    <span className="text-right font-medium">
                      {(col.mobileCell ?? col.cell)(row)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
